import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { auth, db } from '../../firebaseClient';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';

const UserCreationForm = ({ isOpen, onClose, onSubmit }) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      email: '',
      password: '',
      confirm_password: '',
      full_name: '',
      phone_number: '',
      is_admin: false,
      is_super_admin: false,
      is_writer: false,
      is_qa: false,
      is_client: true // Default to client
    }
  });

  const watchPassword = watch('password');

  // Only super admins can create users
  if (!profile?.is_super_admin) {
    return null;
  }

  const handleFormSubmit = async (data) => {
    setLoading(true);
    
    try {
      // Validate password confirmation
      if (data.password !== data.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }

      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const newUser = userCredential.user;

      // Create user profile in Firestore
      const profileData = {
        uid: newUser.uid,
        email: data.email,
        full_name: data.full_name,
        phone_number: data.phone_number,
        is_admin: data.is_admin,
        is_super_admin: data.is_super_admin,
        is_writer: data.is_writer,
        is_qa: data.is_qa,
        is_client: data.is_client,
        created_at: serverTimestamp(),
        created_by: profile.uid,
        created_by_name: profile.full_name || profile.email
      };

      await setDoc(doc(db, 'profiles', newUser.uid), profileData);

      // Determine role for success message
      const roles = [];
      if (data.is_super_admin) roles.push('Super Admin');
      if (data.is_admin) roles.push('Admin');
      if (data.is_writer) roles.push('Writer');
      if (data.is_qa) roles.push('QA');
      if (data.is_client) roles.push('Client');
      
      const roleText = roles.length > 0 ? roles.join(', ') : 'User';
      
      toast.success(`${roleText} account created successfully for ${data.full_name || data.email}`);
      reset();
      onSubmit?.();
      onClose();
    } catch (error) {
      console.error('Error creating user:', error);
      let errorMessage = 'Failed to create user';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email address is already registered';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Create New User"
      icon="UserPlus"
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white border-b border-white/20 pb-2">
            Basic Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Email Address *
              </label>
              <input
                type="email"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
                placeholder="user@example.com"
              />
              {errors.email && (
                <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Full Name *
              </label>
              <input
                type="text"
                {...register('full_name', { required: 'Full name is required' })}
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
                placeholder="John Doe"
              />
              {errors.full_name && (
                <p className="text-red-400 text-sm mt-1">{errors.full_name.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              {...register('phone_number')}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white border-b border-white/20 pb-2">
            Password
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Password *
              </label>
              <input
                type="password"
                {...register('password', { 
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                {...register('confirm_password', { 
                  required: 'Please confirm password',
                  validate: value => value === watchPassword || 'Passwords do not match'
                })}
                className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:border-primary"
                placeholder="••••••••"
              />
              {errors.confirm_password && (
                <p className="text-red-400 text-sm mt-1">{errors.confirm_password.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Role Assignment */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white border-b border-white/20 pb-2">
            Role Assignment
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('is_client')}
                  className="w-4 h-4 text-primary bg-white/20 border-white/30 rounded focus:ring-primary"
                />
                <span className="text-white">Client</span>
                <span className="text-white/60 text-sm">(Can submit assignments)</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('is_writer')}
                  className="w-4 h-4 text-primary bg-white/20 border-white/30 rounded focus:ring-primary"
                />
                <span className="text-white">Writer</span>
                <span className="text-white/60 text-sm">(Can work on assignments)</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('is_qa')}
                  className="w-4 h-4 text-primary bg-white/20 border-white/30 rounded focus:ring-primary"
                />
                <span className="text-white">QA</span>
                <span className="text-white/60 text-sm">(Can review assignments)</span>
              </label>
            </div>

            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('is_admin')}
                  className="w-4 h-4 text-primary bg-white/20 border-white/30 rounded focus:ring-primary"
                />
                <span className="text-white">Admin</span>
                <span className="text-white/60 text-sm">(Can manage assignments)</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('is_super_admin')}
                  className="w-4 h-4 text-primary bg-white/20 border-white/30 rounded focus:ring-primary"
                />
                <span className="text-white">Super Admin</span>
                <span className="text-white/60 text-sm">(Full system access)</span>
              </label>
            </div>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-yellow-400 mt-0.5" />
              <div>
                <p className="text-yellow-400 font-medium">Important:</p>
                <p className="text-yellow-300 text-sm">
                  Users can have multiple roles. Super Admin role grants full system access. 
                  Admin role allows assignment management. Writer role allows working on assignments.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => {
              onClose();
              reset();
            }}
            className="px-6 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Icon name="Loader2" className="animate-spin" size={16} />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Icon name="UserPlus" size={16} />
                <span>Create User</span>
              </>
            )}
          </button>
        </div>
      </form>
    </GlassyModal>
  );
};

export default UserCreationForm;